import i18n from 'i18next'
import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type { EnhancedConfigurationId, EnhancedManCodeId, EnhancedMarketId } from '#api/enhancements/enhancedTmsApiTypes'
import type { ManCode as ConversionServiceManCode, EcmSpecificConversionParameters } from '#api/tmsApiService'
import {
  addPendingConfiguration,
  type ConfigurationVersion,
  createNewConfiguration,
  type LocalConfigurationId,
  setManCode,
  type TruckConfiguration,
  truckConfigurationSlice,
} from '#app/store/slices/configurationSlice'
import { defaultViewForStage, makeRequestConfigurationStep } from '#app/types/Configuration'
import {
  type FullApiConfiguration,
  isFullApiConfiguration,
  isManCodeApiConfiguration,
  isTg3ApiConfiguration,
  type ManCodeApiConfiguration,
  type Tg3ApiConfiguration,
} from '#features/Configuration/ApiConfiguration.ts'
import { makeSerializedError } from '#features/ErrorHandling/errorHelpers'
import { toastError } from '#features/ErrorHandling/toastError'
import { deriveApiConfigurationFromTg3Code } from '#features/LegacyTg3Code/deriveApiConfigurationFromTg3Code'
import { makeConfigurationFromLegacyCode } from '#features/LegacyTg3Code/makeConfigurationFromLegacyCode'
import { mapManCodeToTruckConfiguration } from '#features/ManCode/mapTruckConfiguration'
import { lookupMarketAndLanguageMapping } from '#features/Markets/lookupMarketAndLanguageMapping'
import { loadNationalCodes, type MarketWithNationalCodes } from '#features/NationalCodes/loadNationalCodes'
import { importConfiguredFamiliesFromManCode } from '#features/NationalCodes/nationalCodesToManCode'
import { apiConfigToConfigTarget } from '#pages/Configuration/utils/apiConfigurationHelpers'
import {
  type DateString,
  isValidPeriod,
  isValidPricingDate,
  type PartPeriod,
  type PricingDate,
  periodToString,
  pricingDateToString,
  stringToPeriod,
  stringToPricingDate,
} from '#utils/DateString'
import { logger } from '#utils/logger'
import { makeApiConfigurationFromEcmParameters } from '#utils/makeApiConfigurationFromEcmParameters'
import type { AppListenerApi, AppMiddleware } from '../configurationRequestMiddleware'

export type PendingConfigurationMarketsStep = 'loading-market-mapping' | 'loading-national-codes'
export type PendingConfigurationManLoadingStep = 'retrieving-man-code-infos' | 'convert-man-code'
export type PendingConfigurationTg3LoadingStep = 'retrieving-tg3-code-infos' | 'convert-tg3-code'
export type PendingConfigurationNewConfigurationStep = 'loading-national-codes' | 'creating-configuration'
export type PendingCopyConfigurationStep =
  | 'copying-configuration'
  | 'loading-general-step-data'
  | 'applying-general-step-variants'
  | 'loading-detailed-step-data'
  | 'applying-detailed-step-variants'
export type PendingConfigurationLoadingStep =
  | 'pending'
  | 'error'
  | 'done'
  | PendingConfigurationMarketsStep
  | PendingConfigurationTg3LoadingStep
  | PendingConfigurationManLoadingStep
  | PendingConfigurationNewConfigurationStep
  | PendingCopyConfigurationStep

export type PendingConfigurationTg3ErrorCause = 'received-no-tg3-code' | 'failed-tg3-code-conversion'
export type PendingConfigurationManErrorCause = 'received-no-man-code' | 'received-invalid-man-code' | 'failed-man-code-conversion'
export type PendingConfigurationNewConfigurationErrorCause = 'no-data' | 'failed-request'

export type PendingConfigurationErrorCause =
  | PendingConfigurationManErrorCause
  | PendingConfigurationNewConfigurationErrorCause
  | PendingConfigurationTg3ErrorCause

const updateLoadingState = (
  listenerApi: AppListenerApi,
  localConfigId: LocalConfigurationId,
  {
    uuid,
    ...creation
  }: {
    uuid?: EnhancedConfigurationId
  } & Partial<TruckConfiguration['creation']>,
) => {
  listenerApi.dispatch(truckConfigurationSlice.actions._updatePendingConfiguration({ uuid, creation, id: localConfigId }))
}

const log = logger('🤖 TG3')

const loadTg3Configuration = async (listenerApi: AppListenerApi, pendingConfiguration: TruckConfiguration, tg3Configuration: Tg3ApiConfiguration) => {
  updateLoadingState(listenerApi, pendingConfiguration.id, { loadingStep: 'retrieving-tg3-code-infos' })
  const { tg3Code } = await listenerApi.dispatch(enhancedTmsApiService.endpoints.getTg3CodeById.initiate({ id: tg3Configuration.tg3Code })).unwrap()

  log.log('TG3 Code Response:', tg3Code)

  if (!tg3Code) {
    return { error: 'received-no-tg3-code' } as const
  }

  // try to resolve market and language from tg3 code metadata against admin tool data
  const apiConfiguration = await deriveApiConfigurationFromTg3Code(tg3Code)

  log.log('Using Market Configuration for TG3 Code:', apiConfiguration)

  updateLoadingState(listenerApi, pendingConfiguration.id, { loadingStep: 'convert-tg3-code' })

  const tmsMapping = await listenerApi
    .dispatch(
      enhancedTmsApiService.endpoints.postConvertTg3CodeByLanguage.initiate({
        addFpcMapping: true,
        // TODO this is hardcoded in the backend, should be used from apiConfiguration
        language: 'de',
        // language: apiConfiguration.orderLanguage ?? 'de',
        viewId: apiConfiguration.view,
        excludeHiddenCodes: true,
        tg3ConversionRequest: {
          tg3Code,
          targetFormat: 'MAIC',
        },
      }),
    )
    .unwrap()

  log.log('TG3 Conversion Response:', tmsMapping)

  if (!tmsMapping.productGroups) {
    return { error: 'failed-tg3-code-conversion' } as const
  }

  const convertedConfiguration = await makeConfigurationFromLegacyCode(pendingConfiguration, tg3Code, tmsMapping, apiConfiguration)
  log.log('Converted Configuration:', convertedConfiguration)
  listenerApi.dispatch(createNewConfiguration(convertedConfiguration))

  return {
    result: true,
  }
}

const loadManCode = async (listenerApi: AppListenerApi, pendingConfiguration: TruckConfiguration, manCodeConfiguration: ManCodeApiConfiguration) => {
  const manCodeId = manCodeConfiguration.manCode as EnhancedManCodeId

  updateLoadingState(listenerApi, pendingConfiguration.id, { loadingStep: 'retrieving-man-code-infos' })
  const getCodeRequest = await listenerApi.dispatch(enhancedTmsApiService.endpoints.getCodeById.initiate({ id: manCodeId })).unwrap()

  const manCode = getCodeRequest?.code
  if (!manCode?.configuration) {
    return { error: 'received-no-man-code' } as const
  }

  const ecmParameters = manCode.configuration.ecmParameters
  const partPeriod = manCode.configuration.partPeriod as PartPeriod | undefined
  const desiredDealerDate = manCode.configuration.desiredDealerDate as PricingDate | undefined

  if (!ecmParameters || !partPeriod) {
    return { error: 'received-invalid-man-code' } as const
  }

  const formatSpecificParameters: Required<EcmSpecificConversionParameters> = {
    partPeriodOverwrite: manCodeConfiguration.partPeriod ? stringToPeriod(manCodeConfiguration.partPeriod) : partPeriod,
    // TODO: MANC4TMS-1431
    // @ts-expect-error targetFormat is required but missing in type definition
    targetFormat: 'ECM',
    ecmParametersOverwrites: {
      marketId: manCodeConfiguration.market ?? ecmParameters.marketId,
      configurationTypeId: manCodeConfiguration.configurationType ?? ecmParameters.configurationTypeId,
      languageId: manCodeConfiguration.language ?? ecmParameters.languageId,
      viewId: manCodeConfiguration.view ?? ecmParameters.viewId,
      vehicleTypeId: manCodeConfiguration.vehicleType ?? ecmParameters.vehicleTypeId,
    },
  }

  updateLoadingState(listenerApi, pendingConfiguration.id, { loadingStep: 'convert-man-code' })
  const conversionRequest = await listenerApi.dispatch(
    enhancedTmsApiService.endpoints.postConvertMancode.initiate({
      manConversionTarget: {
        manCode: manCode as ConversionServiceManCode,
        targetFormat: 'ECM',
        formatSpecificParameters,
      },
    }),
  )

  if (conversionRequest.error) {
    throw conversionRequest.error
  }

  const conversionResult = conversionRequest.data?.conversionResult
  if (!conversionResult || !('ecmConfigId' in conversionResult) || !conversionResult.ecmConfigId) {
    return { error: 'failed-man-code-conversion' } as const
  }

  // @TODO: Enhance TMS API Service here
  const configurationId = conversionResult.ecmConfigId as EnhancedConfigurationId
  const partialConfiguration = mapManCodeToTruckConfiguration(manCode, pendingConfiguration.id)
  const keepManCode = manCodeConfiguration.mode !== 'cloned'

  updateLoadingState(listenerApi, pendingConfiguration.id, { loadingStep: 'loading-market-mapping' })
  const { marketId, countryCode, languageCode } = await lookupMarketAndLanguageMapping(
    manCodeConfiguration.orderCountry ?? manCodeConfiguration.market ?? ecmParameters.marketId,
    manCodeConfiguration.orderLanguage ?? manCodeConfiguration.language ?? ecmParameters.languageId,
  )

  updateLoadingState(listenerApi, pendingConfiguration.id, { loadingStep: 'loading-national-codes' })
  let marketData: MarketWithNationalCodes | undefined
  marketData = await loadNationalCodes(manCodeConfiguration.nationalCodeMarketIdOverride ?? marketId ?? (ecmParameters.marketId as EnhancedMarketId))
  marketData = importConfiguredFamiliesFromManCode(marketData, manCode, partialConfiguration, languageCode ?? i18n.language)

  listenerApi.dispatch(
    createNewConfiguration({
      ...pendingConfiguration,
      uuid: configurationId,
      version: partialConfiguration?.version ?? pendingConfiguration.version ?? 1,
      step: {
        stage: manCodeConfiguration.step ?? 'DetailedStep',
        view: defaultViewForStage(manCodeConfiguration.step ?? 'DetailedStep'),
        requested: makeRequestConfigurationStep(manCodeConfiguration.step ?? 'DetailedStep'),
        system: 'unknown',
      },
      apiConfiguration: {
        manCode: keepManCode ? manCodeId : undefined,
        backUrl: manCodeConfiguration.backUrl,
        mode: manCodeConfiguration.mode,
        callingSystem: manCodeConfiguration.callingSystem,
        orderCountry: countryCode,
        orderLanguage: languageCode,
        desiredDealerDate: isValidPricingDate(desiredDealerDate) ? pricingDateToString(desiredDealerDate) : undefined,
        desiredDealerDateIsEstimate: manCode.configuration.desiredDealerDateIsEstimate,
        ...makeApiConfigurationFromEcmParameters(formatSpecificParameters),
      },
      marketData: marketData,
      ...partialConfiguration,
    }),
  )

  updateLoadingState(listenerApi, pendingConfiguration.id, { loadingStep: 'done' })
  return {
    result: {
      id: pendingConfiguration.id,
      manCode: manCodeId,
      version: partialConfiguration?.version ?? 1,
      manUpdateTimestamp: new Date(manCode.updateTimestamp ?? manCode.insertTimestamp ?? Date.now()).getTime(),
    },
  } as const
}

/**
 * Ensures that the part period is set correctly in the appliedApiConfig by requesting it from the API
 */
export async function verifyPartPeriod(dispatch: AppListenerApi['dispatch'], desiredDealerDate: DateString, appliedApiConfig: FullApiConfiguration) {
  try {
    const partPeriodResponse = await dispatch(
      enhancedTmsApiService.endpoints.getProductdataMarketByMarketIdPartPeriodFromDesiredDealerDate.initiate({
        marketId: appliedApiConfig.market,
        vehicleTypeId: appliedApiConfig.vehicleType,
        desiredDealerDate: stringToPricingDate(desiredDealerDate),
      }),
    ).unwrap()
    if (isValidPeriod(partPeriodResponse.partPeriod)) {
      appliedApiConfig.partPeriod = periodToString(partPeriodResponse.partPeriod)
    } else {
      toastError(new Error('Invalid part period'), {
        customMessage: `Received invalid part period from API: ${partPeriodResponse.partPeriod} for desired dealer date ${desiredDealerDate}`,
      })
    }
  } catch (e) {
    toastError(e, {
      customMessage: `Could not retrieve the desired part period for desired dealer date ${desiredDealerDate}`,
    })
  }
}

const loadNewConfiguration = async (listenerApi: AppListenerApi, usedApiConfig: FullApiConfiguration, pendingConfiguration: TruckConfiguration) => {
  const requestStarted = Date.now()
  const appliedApiConfig = {
    ...usedApiConfig,
  }
  updateLoadingState(listenerApi, pendingConfiguration.id, { loadingStep: 'loading-national-codes' })
  const marketData = await loadNationalCodes(appliedApiConfig.nationalCodeMarketIdOverride ?? appliedApiConfig.market)
  updateLoadingState(listenerApi, pendingConfiguration.id, { loadingStep: 'creating-configuration' })
  const response = await listenerApi
    .dispatch(
      enhancedTmsApiService.endpoints.postConfiguration.initiate({
        ecmConfigurationTarget: apiConfigToConfigTarget(appliedApiConfig),
      }),
    )
    .unwrap()

  if (!response.uuid) {
    return { error: 'no-data' } as const
  }
  // always retrieve the part period from the API when the desired dealer date is set
  if (usedApiConfig.desiredDealerDate) {
    await verifyPartPeriod(listenerApi.dispatch, usedApiConfig.desiredDealerDate, appliedApiConfig)
  }

  listenerApi.dispatch(
    createNewConfiguration({
      ...pendingConfiguration,
      uuid: response.uuid,
      version: 1 as ConfigurationVersion,

      step: {
        view: defaultViewForStage(appliedApiConfig.step ?? 'PendingStep'),
        stage: appliedApiConfig.step ?? 'PendingStep',
        requested: makeRequestConfigurationStep(appliedApiConfig.step ?? 'GeneralStep'),
        system: 'unknown',
      },

      title: response.title,
      lastApiInteraction: requestStarted,
      callingApiConfiguration: usedApiConfig,
      apiConfiguration: appliedApiConfig,
      marketData: marketData,
      creation: {
        ...pendingConfiguration.creation,
        loadingStep: 'done',
      },
    }),
  )

  return { result: response.uuid } as const
}

/**
 * Listener handling the creation of configurations when navigating to the creation page
 */
export const configurationCreationListener = (middleware: AppMiddleware) => {
  middleware.startListening({
    matcher: addPendingConfiguration.match,
    effect: async function handleConfigurationCreation(action, listenerApi) {
      if (!addPendingConfiguration.match(action)) {
        return
      }

      const { id: pendingConfigurationId, callingApiConfiguration } = action.payload

      try {
        const pendingConfiguration = listenerApi.getState().truckConfigurations.configurations[pendingConfigurationId]
        if (!pendingConfiguration) {
          return
        }

        if (isTg3ApiConfiguration(callingApiConfiguration)) {
          const { error } = await loadTg3Configuration(listenerApi, pendingConfiguration, callingApiConfiguration)
          if (error) {
            updateLoadingState(listenerApi, pendingConfigurationId, {
              error: {
                loadingErrorCause: error,
              },
              loadingStep: 'error',
            })
          } else {
            updateLoadingState(listenerApi, pendingConfigurationId, { loadingStep: 'done' })
          }
        } else if (isManCodeApiConfiguration(callingApiConfiguration)) {
          const { error, result } = await loadManCode(listenerApi, pendingConfiguration, callingApiConfiguration)
          if (error) {
            updateLoadingState(listenerApi, pendingConfigurationId, {
              error: {
                loadingErrorCause: error,
              },
              loadingStep: 'error',
            })
          }
          // man codes are being kept if it's not cloned. Otherwise cloned configurations overwrite in the former MAN code
          if (result && callingApiConfiguration.mode !== 'cloned') {
            listenerApi.dispatch(setManCode(result))
          }
        }

        if (isFullApiConfiguration(callingApiConfiguration)) {
          const { error, result } = await loadNewConfiguration(listenerApi, callingApiConfiguration, pendingConfiguration)
          if (error) {
            updateLoadingState(listenerApi, pendingConfigurationId, {
              loadingStep: 'error',
              error: {
                loadingErrorCause: error,
              },
            })
          } else if (result) {
            // finally, signal that the creation of the configuration is done and ready
            updateLoadingState(listenerApi, pendingConfigurationId, { loadingStep: 'done' })
          }
        }
      } catch (e) {
        updateLoadingState(listenerApi, pendingConfigurationId, {
          error: {
            loadingErrorCause: 'failed-request',
            ...makeSerializedError(e),
          },
          loadingStep: 'error',
        })
      }
    },
  })
}
